import { Placement } from '@floating-ui/core'
import cx from 'classnames'
import { EditorColorPicker } from 'common/Prose/components/EditorColorPicker/EditorColorPicker'
import { CALLOUT_COLOR_OPTIONS, DEFAULT_CALLOUT_COLOR } from 'common/Prose/constants/colorOptions'
import { useCalloutButton } from 'components/DocRightRail/screens/FormatMenu/hooks/useCalloutButton'
import { observer } from 'mobx-react-lite'
import Styles from './DoubleActionButtons.module.scss'
import { ChevronDown } from 'almanac-ui/icons/12'
import { useHover } from '@react-aria/interactions'
import { SimpleDropdown } from '../SimpleDropdown/SimpleDropdown'
import { IconButton } from 'components/IconButton/IconButton'

const Icon = ({ colorCount }: { colorCount?: number }) => {
  const { activeColors, active } = useCalloutButton()
  const activeCalloutColors = activeColors.slice(0, colorCount)
  const colorWidth = 100 / activeCalloutColors.length

  return (
    <div className={Styles.doubleActionButton}>
      {activeCalloutColors.map((color) => (
        <span
          className={cx(Styles.activeIndicator)}
          key={`activeColorIndicator-${color}`}
          style={
            {
              flexBasis: `${colorWidth}%`,
              '--color': color,
              '--hover-color': `linear-gradient(0deg, var(--neutral-10) 0%, var(--neutral-10) 100%), ${color}`,
            } as React.CSSProperties
          }
        />
      ))}
      {!active && <span className={cx(Styles.activeIndicator)} />}
      <span className={cx(Styles.buttonText, { [Styles.active]: active })}>Callout</span>
    </div>
  )
}

export const CalloutButton = observer(function CalloutButton({
  colorCount = 4,
  dropdownPlacement = 'bottom',
}: {
  colorCount?: number
  dropdownPlacement?: Placement
}) {
  const { activeColors, active, disabled, setColor, removeColor } = useCalloutButton()
  const { hoverProps, isHovered } = useHover({})

  const onClick = () => {
    if (active) {
      removeColor()
    } else {
      setColor(DEFAULT_CALLOUT_COLOR)
    }
  }

  return (
    <div className={cx(Styles.doubleActionButton, Styles.calloutButton)}>
      <IconButton
        className={cx(Styles.directActionButton, { [Styles.hovered]: isHovered })}
        variant="tertiary"
        size="md"
        onPress={onClick}
        aria-label="Add a box with a colored background"
        icon={() => <Icon colorCount={colorCount} />}
      />

      <SimpleDropdown
        buttonClassName={Styles.arrow}
        hasChevron={false}
        disabled={disabled}
        unStyledButton
        tooltip={{ label: 'Callout' }}
        placement={dropdownPlacement}
        {...hoverProps}
      >
        <ChevronDown />
        <EditorColorPicker
          aria-label={(color: string) => `Change callout color to ${color}`}
          colorOptions={CALLOUT_COLOR_OPTIONS}
          activeColors={activeColors}
          setColor={(color) => {
            setColor(color)
          }}
          noColorButtonPos="bottom"
          noColorButtonLabel="Remove"
          defaultColor={DEFAULT_CALLOUT_COLOR}
          removeColor={() => {
            removeColor()
          }}
          includeDropdownStyles={false}
        />
      </SimpleDropdown>
    </div>
  )
})
